'use client';

import { useCallback, useState } from 'react';
import { useLazyGetEntityDetailsByIdQuery } from 'src/api/redux';
import { useStopSelection, type EntityDetails } from './';

export interface UseAddProviderToRouteReturn {
  /**
   * Add a provider to the route by fetching entity details first
   * Returns true if successful, false if failed
   */
  addProviderToRoute: (providerId: string | number) => Promise<boolean>;
  /** Whether a fetch is in progress */
  isLoading: boolean;
  /** Error message if the last operation failed */
  error: string | null;
}

/**
 * Hook to add a provider to a route by first fetching entity details
 * to get the address_key and all available addresses.
 *
 * This handles the async flow of:
 * 1. Fetching entity details via useGetEntityDetailsByIdQuery
 * 2. Creating a stop with address_key from primary_address
 * 3. Storing available addresses for later address changes
 */
const useAddProviderToRoute = (): UseAddProviderToRouteReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [fetchEntityDetails] = useLazyGetEntityDetailsByIdQuery();
  const { addEntityToRoute, isInRoute } = useStopSelection();

  const addProviderToRoute = useCallback(
    async (providerId: string | number): Promise<boolean> => {
      // Check if already in route
      if (isInRoute(String(providerId))) {
        return false;
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await fetchEntityDetails({
          id: String(providerId),
        }).unwrap();

        if (!result) {
          setError('Failed to fetch provider details');
          return false;
        }

        // Add to route using entity details (includes address_key)
        addEntityToRoute(result as EntityDetails);
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to add provider to route';
        setError(errorMessage);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchEntityDetails, addEntityToRoute, isInRoute]
  );

  return {
    addProviderToRoute,
    isLoading,
    error,
  };
};

export default useAddProviderToRoute;
